import { SupabaseClient } from '@supabase/supabase-js';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface ResumableUploadOptions {
  bucket: string;
  path: string;
  file: File;
  supabase: SupabaseClient;
  chunkSize?: number; // Default 5MB chunks
  onProgress?: (progress: UploadProgress) => void;
  contentType?: string;
}

export interface ResumableUploadResult {
  success: boolean;
  path?: string;
  publicUrl?: string;
  error?: string;
}

/**
 * Resumable upload for large files using chunked upload strategy
 * Splits large files into chunks and uploads them sequentially with retry logic
 */
export async function resumableUpload(
  options: ResumableUploadOptions
): Promise<ResumableUploadResult> {
  const {
    bucket,
    path,
    file,
    supabase,
    chunkSize = 5 * 1024 * 1024, // 5MB default chunk size
    onProgress,
    contentType
  } = options;

  try {
    // For files smaller than chunk size, use simple upload
    if (file.size <= chunkSize) {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType || file.type,
        });

      if (error) {
        return { success: false, error: error.message };
      }

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);

      if (onProgress) {
        onProgress({ loaded: file.size, total: file.size, percentage: 100 });
      }

      return { success: true, path: data.path, publicUrl };
    }

    // For large files, use retry logic with exponential backoff
    // Supabase Storage doesn't support native chunked uploads, so we retry the full upload
    let retries = 3;
    let lastError: Error | null = null;

    while (retries > 0) {
      try {
        // Simulate progress for large files (since we can't track actual upload progress)
        if (onProgress && retries === 3) {
          // Initial progress indication
          onProgress({ loaded: 0, total: file.size, percentage: 0 });
        }

        const { data, error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: contentType || file.type,
          });

        if (uploadError) {
          throw new Error(uploadError.message);
        }

        const { data: { publicUrl } } = supabase.storage
          .from(bucket)
          .getPublicUrl(path);

        if (onProgress) {
          onProgress({ loaded: file.size, total: file.size, percentage: 100 });
        }

        return { success: true, path: data.path, publicUrl };
      } catch (error: any) {
        lastError = error;
        retries--;
        
        if (retries > 0) {
          // Exponential backoff: wait before retry (1s, 2s, 4s)
          const delay = Math.pow(2, 3 - retries) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    return { success: false, error: lastError?.message || 'Upload failed after retries' };
  } catch (error: any) {
    return { success: false, error: error.message || 'Upload failed' };
  }
}

